import { useCallback } from 'react';

import { useClient } from 'urql';

import { useFavorites } from '@order/features-orders';

import { useFavoriteLineItemMutation } from '../../../OrderStatusScreen/graphql/OrderStatus.generated';
import {
  FavoritesDocument,
  type FavoritesQuery,
  type FavoritesQueryVariables,
} from '../../GraphQL/Favorites.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Connects the account favorites to the shared favorites state machine, which
 * handles the initial load, pagination and favorite state toggling.
 */
export const useAccountFavorites = () => {
  const client = useClient();

  const [, favoriteLineItem] = useFavoriteLineItemMutation();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const fetchFavorites = useCallback<FetchFavorites>(
    async (context) => {
      const { page } = context;

      const { data } = await client
        .query<FavoritesQuery, FavoritesQueryVariables>(
          FavoritesDocument,
          { input: { page } },
          { requestPolicy: 'network-only' },
        )
        .toPromise();

      const favoritesData = data?.favoritedLineItems;

      if (!Array.isArray(favoritesData)) {
        return { favorites: [] };
      }

      return { favorites: favoritesData };
    },
    [client],
  );

  const toggleFavorite = useCallback<ToggleFavorite>(
    async (_context, event) => {
      const { id, isFavorite, productId } = event;

      const { data } = await favoriteLineItem({
        input: { lineItemId: id, productId, favorited: isFavorite },
      });

      const updatedLineItem = data?.favoriteLineItem;

      if (updatedLineItem?.__typename !== 'LineItem') {
        return null;
      }

      return { id: updatedLineItem.id, isFavorite: updatedLineItem.favorited };
    },
    [favoriteLineItem],
  );

  return useFavorites<Favorite>({ fetchFavorites, toggleFavorite });
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FetchFavorites = UseFavoritesParams['fetchFavorites'];

type ToggleFavorite = UseFavoritesParams['toggleFavorite'];

type Favorite = FavoritesQuery['favoritedLineItems'][number];

type UseFavoritesParams = Parameters<typeof useFavorites<Favorite>>[0];
